"use client"
import Footer from "@/components/UI/footer/footer"
import Topbar from "@/components/UI/navbar/topbar"
import {
  AcademicCapIcon,
  BoltIcon,
  ClipboardDocumentCheckIcon,
  NewspaperIcon,
} from "@heroicons/react/24/outline"
import { CalendarIcon } from "@heroicons/react/24/outline"
import { StarIcon } from "@heroicons/react/24/outline"
import React, { useState } from "react"

import faqData from "@/content/faq.json" assert { type: "json" }
import { CalculatorIcon } from "@heroicons/react/24/outline"
import ContentElement from "@/components/UI/ContentElement/ContentElement"
import ToolElement from "@/components/home/tools/tool"
import IdeaElement from "@/components/home/ideals/idea"
import HeroSection from "@/components/hero"
import QuestionElement from "@/components/home/faq/question"
import { MagnifyingGlassCircleIcon } from "@heroicons/react/24/outline"
import { SEOUtil } from "@/utils/SEOUtil";

const Home: React.FC = () => {
  const [visibleAnswers, setVisibleAnswers] = useState(-1)

  const handleVisible = (number: number) => {
    setVisibleAnswers(number)
  }

  return (
    <>
      <SEOUtil name="Szkolny Przewodnik" url="" description="Szkolny Przewodnik to Twoje wsparcie i doradztwo w wyborze kariery. Znajdziesz tutaj inspiracje, porady i narzędzia dopasowane do Twoich potrzeb!" />
      <Topbar />
      <main>
        <HeroSection
          src='/home/baner.webp'
          heading='SzkolnyPrzewodnik,'
          other={{
            italic: "Edukacyjna przyszłość\nzaczyna się tutaj!",
            text: "Szkolny Przewodnik to platforma edukacyjna stworzona z myślą o uczniach szkół podstawowych i ich rodzicach.",
            href: "#functions",
          }}
          className='mb-16 whitespace-pre-line sm:whitespace-normal'
        />


        <ContentElement
          className='p-20 m-0 text-center mb-40'
          heading={"Skorzystaj z naszych narzędzi w życiu codziennym"}
          subheading={"Oferowane funkcje"}
          childrenClassName='grid grid-cols-2 2xl:grid-cols-3 justify-center text-left mx-0 my-auto gap-40'
          id='functions'>
          <ToolElement
            href='/app/doradztwo'
            src='/home/functions/school-img.webp'
            alt='School Icon'
            icon={<AcademicCapIcon className='size-8' />}
            heading='Doradztwo'
            text='Wybierz za pomocą naszego narzędzia szkołę, która do ciebie pasuje.'
          />
          <ToolElement
            href='/app/nawigator'
            src='/home/functions/nawigator-img.webp'
            alt='Notebook Icon'
            icon={<MagnifyingGlassCircleIcon className='size-8' />}
            heading='Nawigator'
            text='Odkryj najlepsze szkoły i kierunki w swoim regionie i otwórz drzwi do nowych możliwości.'
          />
          <ToolElement
            href='/app/kalendarz'
            src='/home/functions/calendar-img.webp'
            alt='Calendar Icon'
            icon={<CalendarIcon className='size-8' />}
            heading='Kalendarz'
            text='Sprawdź dni wolne i zaplanuj swój rok szkolny perfekcyjnie już teraz.'
          />
          <ToolElement
            href='/app/testy'
            src='/home/functions/testy-img.webp'
            alt='Question mark Icon'
            icon={<NewspaperIcon className='size-8' />}
            heading='Testy'
            text='Zmierz się z pytaniami, które przygotują Cię na wszystko.'
          />
          <ToolElement
            href='/app/kalkulator'
            src='/home/functions/calculator-img.webp'
            alt='Calculator Icon'
            icon={<CalculatorIcon className='size-8' />}
            heading='Kalkulator'
            text='Za pomocą naszego kalkulatora obliczysz swoje punkty do szkoły średniej.'
          />
          <ToolElement
            href='/app/rekrutacja'
            src='/home/functions/rekrutacja.webp'
            alt='Rekrutacja do szkoły średniej'
            icon={<ClipboardDocumentCheckIcon className='size-8' />}
            heading='Rekrutacja'
            text='Sprawdź terminy rekrutacji i nie przegap żadnego etapu.'
          />
        </ContentElement>

        <ContentElement
          className='p-20 m-0 mb-20 bg-sectionbg'
          heading={"Chcemy pomóc użytkownikowi takiemu jak ty!"}
          subheading={"Nasze cele"}
          childrenClassName='container grid grid-cols-2 justify-center gap-32 items-center'
          isWhite={true}>
          <IdeaElement
            src={"/home/ideals/choice.webp"}
            alt={"Wybór tabletek biała czy żółta?"}
            icon={<BoltIcon className='size-10 text-icons' />}
            heading={"Szybki i dobry wybór"}
            text={
              "Narzędzie do wybierania szkoły znajdzie w twoim Powiecie szkołę, która według twoich wymagań, zainteresowań oraz umiejętnośći pasuje idealnie do Ciebie!"
            }
          />
          <IdeaElement
            src={"/home/ideals/nawigation.webp"}
            alt={"Torowisko"}
            icon={<MagnifyingGlassCircleIcon className='size-10 text-icons' />}
            heading={"Prosta nawigacja"}
            text={
              "Nasze narzędzia szybko nawigują cię na nowe ścieżki Twojego życia."
            }
            reverse
          />

          <IdeaElement
            src={"/home/ideals/light.webp"}
            alt={"Przyszłość"}
            icon={<StarIcon className='size-10 text-icons' />}
            heading={"Świetlana Przyszłość"}
            text={
              "Czy to diament? Nie! To twoja przyszłość oszlifowana jak brylant przy naszej pomocy."
            }
          />
        </ContentElement>

        <ContentElement
          heading='Masz jakieś pytania?'
          subheading='Sekcja FAQ'
          className='p-20 m-0 mb-40'
          childrenClassName='grid md:grid-cols-1 lg:grid-cols-2 items-start gap-14'>
          <div className='relative z-10 min-h-200 w-full rounded-xl p-5 flex flex-col items-center shadow-custom'>
            <ul className='flex flex-col gap-7 transition-all duration-short'>
              {faqData.map((item, index) => (
                <QuestionElement
                  index={index}
                  question={item.question}
                  answer={item.answer}
                  visibleAnswer={visibleAnswers}
                  handleVisible={handleVisible}
                  key={index}
                />
              ))}
            </ul>
          </div>

          <div className='w-full h-200 p-5 relative z-10 shadow-2xl rounded-xl flex flex-col items-center justify-center bg-linear-to-br from-main to-btn'>
            <h2 className='text-4xl font-bold text-white16 mb-4'>
              Masz jeszcze jakieś pytania?
            </h2>
            <p className='text-3xl text-white48 text-center mb-8'>
              Wypełnij formularz kontaktowy, a udzielimy ci odpowiedzi.
            </p>
            <a
              href='/contact'
              className='text-3xl font-semibold text-white5 bg-btn no-underline inline-block px-3 py-4 rounded-3xl transition-all hover:scale-110 '>
              Formularz
            </a>
          </div>
        </ContentElement>
      </main>
      <Footer />
    </>
  )
}

export default Home
